import * as React from "react";
import ModelSelector from "./ModelSelector";
import UserProfilePanel from "./UserProfilePanel";
import { folderPath } from "../services/folderTree";
import type { UserProfile, ModelChoice, UserSettings, OwuiFolder } from "@lupp/shared";

function userInitials(user: UserProfile): string {
  const parts = user.name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return user.name.slice(0, 2).toUpperCase();
}

export interface TopBarProps {
  selectedModelId: string;
  models: ModelChoice[];
  onModelSelect: (model: ModelChoice) => void;
  onNewChat: () => void;
  onToggleHistory: () => void;
  historyOpen: boolean;
  user?: UserProfile | null;
  userSettings?: UserSettings | null;
  folders: OwuiFolder[];
  activeFolderId: string | null;
  onClearProject: () => void;
  chatTitle: string;
}

/**
 * Active-project breadcrumb as flowing text (no pill), using all available width:
 *   Projekt › Unterordner › … › tiefster Ordner › Chatname
 * Truncation priority (what survives when space is tight, via flex-shrink):
 *   1. project name (root) — always readable (never shrinks)
 *   2. deepest folder — kept if possible (middle path collapses first)
 *   3. full intermediate path — shown when there's room
 *   4. chat name (tail) — lowest priority, ellipsised/dropped first
 */
const Sep = (): JSX.Element => <span className="breadcrumb__sep" aria-hidden="true">›</span>;

function ProjectBreadcrumb({
  folders,
  activeFolderId,
  chatTitle,
  onClear,
}: {
  folders: OwuiFolder[];
  activeFolderId: string;
  chatTitle: string;
  onClear: () => void;
}): JSX.Element | null {
  const path = folderPath(folders, activeFolderId);
  if (path.length === 0) return null;
  const root = path[0];
  const leaf = path.length > 1 ? path[path.length - 1] : undefined;
  const middle = path.slice(1, path.length - 1); // folders between root and leaf
  const chat = chatTitle.trim() || "Neuer Chat";
  const full = [...path.map((f) => f.name), chat].join(" › ");

  return (
    <div className="breadcrumb" title={full}>
      <span className="breadcrumb__root">{root.name}</span>
      {middle.length > 0 && (
        <span className="breadcrumb__mid">
          <Sep />
          {middle.map((f) => f.name).join(" › ")}
        </span>
      )}
      {leaf && (
        <span className="breadcrumb__leaf">
          <Sep />
          {leaf.name}
        </span>
      )}
      <span className="breadcrumb__chat">
        <Sep />
        {chat}
      </span>
      <button className="breadcrumb__remove" type="button" aria-label="Projekt verlassen" title="Projekt verlassen" onClick={onClear}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

interface TopBarState {
  profilePanelOpen: boolean;
}

export default class TopBar extends React.Component<TopBarProps, TopBarState> {
  constructor(props: TopBarProps) {
    super(props);
    this.state = { profilePanelOpen: false };
  }

  handleAvatarClick = (): void => {
    this.setState((s) => ({ profilePanelOpen: !s.profilePanelOpen }));
  };

  handleClosePanel = (): void => {
    this.setState({ profilePanelOpen: false });
  };

  render(): JSX.Element {
    const { selectedModelId, models, onModelSelect, onNewChat, onToggleHistory, historyOpen, user, userSettings = null, folders, activeFolderId, onClearProject, chatTitle } = this.props;
    const { profilePanelOpen } = this.state;

    return (
      <header className="topbar">
        {/* left: model selector + active project breadcrumb */}
        <div className="topbar__left">
          <ModelSelector selectedId={selectedModelId} models={models} onSelect={onModelSelect} />
          {activeFolderId && <ProjectBreadcrumb folders={folders} activeFolderId={activeFolderId} chatTitle={chatTitle} onClear={onClearProject} />}
        </div>

        {/* right: history toggle + new chat + avatar */}
        <div className="topbar__right">
          <button
            className={`icon-btn${historyOpen ? " icon-btn--active" : ""}`}
            type="button"
            aria-label="Chat-Verlauf"
            title="Chat-Verlauf"
            aria-pressed={historyOpen}
            onClick={onToggleHistory}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M10 2.5a7.5 7.5 0 1 1 0 15 7.5 7.5 0 0 1 0-15m0 1a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13M10 5a.5.5 0 0 1 .5.5v4.169l3.197 1.371.088.05a.5.5 0 0 1-.386.9l-.096-.03-3.5-1.5A.5.5 0 0 1 9.5 10V5.5A.5.5 0 0 1 10 5" />
            </svg>
          </button>
          <button className="icon-btn" type="button" aria-label="Neuer Chat" title="Neuer Chat" onClick={onNewChat}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
          {/* TODO(reactivate): "Mehr"-Button vorübergehend ausgeblendet — Funktion fehlt noch.
          <button className="icon-btn" type="button" aria-label="Mehr Optionen" title="Mehr">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="5" cy="12" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="19" cy="12" r="1.5" />
            </svg>
          </button>
          */}
          {/* TODO(reactivate): Einstellungen-Button vorübergehend ausgeblendet — Settings-Panel fehlt noch.
          <button className="icon-btn" type="button" aria-label="Einstellungen" title="Einstellungen">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="14" y2="12" />
              <line x1="4" y1="18" x2="18" y2="18" />
              <circle cx="18" cy="6" r="2" fill="currentColor" stroke="none" />
              <circle cx="17" cy="12" r="2" fill="currentColor" stroke="none" />
              <circle cx="20" cy="18" r="2" fill="currentColor" stroke="none" />
            </svg>
          </button>
          */}
          <button
            className="topbar__avatar-btn"
            type="button"
            aria-label={user ? `Profil: ${user.name}` : "Profil"}
            title={user ? `${user.name} (${user.email})` : undefined}
            /* TODO(reactivate): Avatar-Klick (Profil-Panel) vorübergehend deaktiviert.
            onClick={this.handleAvatarClick} */
          >
            {user?.profileImageUrl ? (
              <img
                className="topbar__avatar-img"
                src={user.profileImageUrl}
                alt="Profilbild"
              />
            ) : (
              <div className="topbar__avatar" aria-hidden="true">
                {user ? userInitials(user) : "…"}
              </div>
            )}
          </button>
        </div>

        {user && (
          <UserProfilePanel
            user={user}
            settings={userSettings}
            open={profilePanelOpen}
            onClose={this.handleClosePanel}
          />
        )}
      </header>
    );
  }
}
